import clsx from 'clsx';
import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import ReactGA from 'react-ga4';
import TagManager from 'react-gtm-module';
import { useRouter } from 'next/router';
import {
  Typography,
  Grid,
  Card,
  CardContent,
  Chip,
  Radio,
  RadioGroup,
  FormControlLabel,
  FormControl,
  MenuItem,
  Divider,
  Box,
  TextField,
  Button,
} from '@mui/material';
import { ChevronLeft } from '@mui/icons-material';
import AlertAlt from 'components/AlertAlt';
import ThemeButton from 'components/Button';
import {
  PlanEnum,
  PaymentMethodEnum,
  BillingIntervalEnum,
  CouponStatusEnum,
  GatewayEnum,
} from 'enums';
import PaymentService from 'services/PaymentService';
import { getEnv } from 'utils';
import { resolvePlan, getPlanData, resolveBillingInterval } from 'utils/plan';
import { useStyles } from './styles';
import getCleverTap from 'utils/clevertap';
import { useAuthUser } from 'features/auth/AuthUserContext';

const Props = {
  coupon: PropTypes.shape({
    gateway: PropTypes.string.isRequired,
    context: PropTypes.string,
    code: PropTypes.string.isRequired,
    percent: PropTypes.number.isRequired,
    plan: PropTypes.oneOf(Object.values(PlanEnum)),
    billingInterval: PropTypes.oneOf(Object.values(BillingIntervalEnum)),
    status: PropTypes.oneOf(Object.values(CouponStatusEnum)).isRequired,
  }),
  onAllPlansButtonClick: PropTypes.func.isRequired,
};

export default function PageChina(props) {
  const classes = useStyles();
  const router = useRouter();
  const { user } = useAuthUser();
  const [paymentMethod, setPaymentMethod] = useState(PaymentMethodEnum.ALIPAY);
  const [plan, setPlan] = useState(PlanEnum.RESEARCHER);
  const [billingInterval, setBillingInterval] = useState(BillingIntervalEnum.YEAR);
  const [coupon, setCoupon] = useState();
  const [isBusy, setIsBusy] = useState(false);
  const planData = getPlanData(plan, billingInterval, user.country_code);
  const paymentService = new PaymentService();
  const clevertap = getCleverTap();

  const getDiscountLabel = (discountPercent) => {
    switch (discountPercent) {
      case 45.6:
        return '34';
      case 69.1:
        return '23';
      case 28.4:
        return '14';
      case 43.2:
        return '37';
      default:
        return '';
    }
  };

  function calculateCouponTotalPrice() {
    if (coupon) {
      const discountAmount = planData.totalPrice * (coupon.percent / 100);

      return Math.round(planData.totalPrice - discountAmount);
    }

    return planData.totalPrice;
  }

  function sendSubmitTrackingEvents() {
    ReactGA.event({
      category: 'checkout',
      action: `order_clicked_${planData.label}`,
      label: 'When the Complete AliPay Subscription button is clicked',
      value: user.id,
    });

    ReactGA.event({
      category: 'checkout',
      action: `order_clicked_${billingInterval}`,
      label: 'When the Complete AliPay Subscription button is clicked',
      value: user.id,
    });
  }

  async function handleSetupIntent(id) {
    const { data: setupIntent } = await paymentService.getStripeSetupIntent(id);
    const { payment_method: paymentMethod } = setupIntent;

    const response = await paymentService.stripe({
      plan,
      billingInterval,
      paymentMethodId: paymentMethod,
      paymentMethodType: PaymentMethodEnum.ALIPAY,
      couponCode: coupon?.code,
    });

    if (response.data.status === 'active') {
      handleSuccededPayment();
    } else {
      console.log('subscription error', response.data);
    }
  }

  function makeAlipayPaymentUrl() {
    const url = `${getEnv('MTG_URL_API')}/payments/alipay/alipay_pc/${
      user.id
    }/${plan}/${billingInterval}?returnUrl=${makeSuccededPaymentRedirectUrl({ fullUrl: true })}`;
    return url;
  }

  function makeSuccededPaymentRedirectUrl(
    options = { pendingConfirmation: false, fullUrl: false },
  ) {
    let url;

    if (options.pendingConfirmation) {
      url = `/my-account/overview?processingPayment=1&plan=${plan}`;
    } else {
      url = `/my-account/overview?succededPayment=1&plan=${plan}`;
    }

    if (coupon?.context) {
      url += `&couponContext=${coupon.context}`;
    }

    return options.fullUrl ? `${window.location.origin}/app${url}` : url;
  }

  function sendSuccededPaymentTrackingEvents(transactionId) {
    TagManager.dataLayer({
      dataLayer: {
        event: 'checkout',
        ecommerce: {
          purchase: {
            actionField: {
              id: transactionId || '',
              affiliation: 'Web',
              revenue: planData.totalUsd,
              tax: 0,
              shipping: 0,
            },
            products: [
              {
                name: planData.label,
                id: planData.id,
                price: planData.totalUsd,
                brand: 'Web',
                category: 'Subscription',
                variant: billingInterval,
                quantity: 1,
              },
            ],
          },
        },
      },
    });

    ReactGA.event({
      category: 'checkout',
      action: `payment_succeeded_${planData.label}`,
      label: 'When the subscription is complete',
      value: user.id,
    });

    ReactGA.event({
      category: 'checkout',
      action: `payment_succeeded_${billingInterval}`,
      label: 'When the subscription is complete',
      value: user.id,
    });

    clevertap.event.push('Purchase', {
      Product: 'MTG',
      Email: user.email,
      'Country code': user.country_code,
      URL: router.asPath,
      'Billing Interval': billingInterval,
      Plan: planData.label,
      Description: 'Payment Succeeded',
      Locale: 'checkout china',
    });

    if (coupon?.context === 'black_november') {
      ReactGA.event({
        category: 'checkout',
        action: 'black_november_order_finished',
        label: 'when payment for the black november promotion is successfully completed',
        value: user.id,
      });

      clevertap.event.push('Purchase', {
        Product: 'MTG',
        Email: user.email,
        'Country code': user.country_code,
        URL: router.asPath,
        'Billing Interval': billingInterval,
        Plan: planData.label,
        Description: 'Black Friday Payment Succeeded',
        Locale: 'checkout china',
        coupon: 'black_november',
      });
    }
  }

  function handleSuccededPayment(transactionId) {
    sendSuccededPaymentTrackingEvents(transactionId);

    router.push(makeSuccededPaymentRedirectUrl());
  }

  async function handleSubmit(event) {
    event.preventDefault();

    sendSubmitTrackingEvents();
    setIsBusy(true);

    if (paymentMethod === PaymentMethodEnum.ALIPAY) {
      router.push(makeAlipayPaymentUrl());
    }
  }

  function handlePaymentMethodChange(event) {
    setPaymentMethod(event.target.value);
  }

  useEffect(() => {
    setPlan(resolvePlan(router.query.plan));
    setBillingInterval(
      resolveBillingInterval(router.query.billingInterval, resolvePlan(router.query.plan)),
    );

    if (
      router.query.redirect_status === 'succeeded' &&
      router.query.setup_intent &&
      router.query.setup_intent_client_secret
    ) {
      handleSetupIntent(router.query.setup_intent);
    }
  }, [router.query]);

  useEffect(() => {
    if (props.coupon && props.coupon.status === CouponStatusEnum.AVAILABLE) {
      setCoupon(props.coupon);

      if (props.coupon.gateway === GatewayEnum.STRIPE) {
        setPaymentMethod(PaymentMethodEnum.CARD);
      }

      if (props.coupon.gateway === GatewayEnum.PAYPAL) {
        setPaymentMethod(PaymentMethodEnum.PAYPAL);
      }

      if (props.coupon.plan) {
        setPlan(props.coupon.plan);
      }

      if (props.coupon.billingInterval) {
        setBillingInterval(props.coupon.billingInterval);
      }
    }

    setCoupon(props.coupon);
  }, [props]);

  return (
    <>
      <Button
        className={classes.allPlansButton}
        color="primary"
        startIcon={<ChevronLeft />}
        onClick={props.onAllPlansButtonClick}
      >
        所有计划
      </Button>

      <Box mb={6} textAlign="center">
        <Typography variant="h5">
          <b>将思维与创意具象化</b>
        </Typography>
        <Typography>描摹出精细的科研配图</Typography>
      </Box>

      <form onSubmit={handleSubmit}>
        <Grid container={true} spacing={3}>
          <Grid item={true} xs={12} md={8}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom={true}>
                  <b>订阅方案</b>
                </Typography>
                <Typography>
                  <b>{planData.label === 'Student' && '学生'}</b>
                  <b>{planData.label === 'Researcher' && '研究者'}</b>
                  <b>{planData.label === 'Teams & Labs' && '团队'}</b>
                </Typography>

                {billingInterval === BillingIntervalEnum.YEAR && (
                  <Typography className={classes.greyText}>
                    ￥ {planData.monthlyPrice} /月 (年费共计￥ {planData.totalPrice}){' '}
                    {!coupon && (
                      <Chip
                        label={`可节省${getDiscountLabel(planData.discountPercent)}%`}
                        color="secondary"
                        size="small"
                        component="span"
                      />
                    )}
                  </Typography>
                )}

                {billingInterval === BillingIntervalEnum.MONTH && !coupon && (
                  <Typography className={classes.greyText}>
                    ￥ {planData.monthlyPrice} /月
                  </Typography>
                )}

                {billingInterval === BillingIntervalEnum.MONTH && coupon && (
                  <Typography className={classes.greyText}>
                    <del>
                      {planData.currency} {planData.monthlyPrice}
                    </del>{' '}
                    {planData.currency} {calculateCouponTotalPrice()} / month{' '}
                    <Chip
                      label={`+${coupon.percent}% OFF`}
                      color="secondary"
                      size="small"
                      component="span"
                    />
                  </Typography>
                )}

                {billingInterval === BillingIntervalEnum.QUARTER && !coupon && (
                  <Typography className={classes.greyText}>
                    ￥ {planData.monthlyPrice} /月 (年费共计￥ {planData.totalPrice}){' '}
                  </Typography>
                )}

                {billingInterval === BillingIntervalEnum.QUARTER && coupon && (
                  <Typography className={classes.greyText}>
                    ￥ {planData.monthlyPrice} /月 (年费共计￥ {planData.totalPrice}){' '}
                    <Chip
                      label={`可节省${planData.discountPercent}%`}
                      color="secondary"
                      size="small"
                      component="span"
                    />
                  </Typography>
                )}
              </CardContent>
            </Card>

            <Card className={classes.paymentDetailsCard}>
              <CardContent>
                <Typography variant="h6" gutterBottom={true}>
                  <b>付款详情</b>
                </Typography>

                <FormControl variant="standard" className={classes.paymentMethodFormControl}>
                  <RadioGroup
                    name="paymentMethod"
                    value={paymentMethod}
                    onChange={handlePaymentMethodChange}
                  >
                    <FormControlLabel
                      className={clsx(classes.paymentOption, {
                        [classes.paymentOptionDisabled]:
                          coupon && coupon.gateway !== GatewayEnum.STRIPE,
                      })}
                      label={
                        <img
                          src="/images/alipay-china.png"
                          alt="Alipay Logo"
                          width={70}
                          height={25}
                        />
                      }
                      disabled={coupon && coupon.gateway !== GatewayEnum.STRIPE}
                      value={PaymentMethodEnum.ALIPAY}
                      control={<Radio color="secondary" />}
                    />
                  </RadioGroup>
                </FormControl>

                <Box mb={2}>
                  <Typography className={classes.greyText}>
                    支付宝不支持自动续费/扣款，仅当次过账有效。订阅到期后，如欲续订，需返回此页面重新支付。
                  </Typography>
                </Box>

                <Typography className={classes.greyText}>
                  点击“确认购买”后，您将前往第三方页面完成付款。
                </Typography>
              </CardContent>
            </Card>
          </Grid>

          <Grid item={true} xs={12} md={4}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom={true}>
                  <b>支付方案</b>
                </Typography>

                <TextField
                  fullWidth={true}
                  select={true}
                  variant="outlined"
                  value={billingInterval}
                  onChange={(event) => setBillingInterval(event.target.value)}
                >
                  <MenuItem value={BillingIntervalEnum.YEAR}>年付</MenuItem>

                  <MenuItem value={BillingIntervalEnum.MONTH}>月付</MenuItem>

                  {plan !== PlanEnum.LAB_RESEARCHER && (
                    <MenuItem value={BillingIntervalEnum.QUARTER}>季付</MenuItem>
                  )}
                </TextField>

                <Box my={2}>
                  {billingInterval === BillingIntervalEnum.YEAR && (
                    <AlertAlt icon={false}>
                      年付可省{getDiscountLabel(planData.discountPercent)}%
                    </AlertAlt>
                  )}

                  {billingInterval === BillingIntervalEnum.MONTH && plan === 'S' && (
                    <AlertAlt icon={false} severity="warning">
                      换至年付，即刻节省 157/年
                    </AlertAlt>
                  )}

                  {billingInterval === BillingIntervalEnum.MONTH && plan !== 'S' && (
                    <AlertAlt icon={false} severity="warning">
                      换至年付，即刻节省{' '}
                      {
                        getPlanData(
                          plan,
                          BillingIntervalEnum.YEAR,
                          paymentMethod,
                          user.country_code,
                        ).discount
                      }
                      /年
                    </AlertAlt>
                  )}
                </Box>

                <Divider />

                {!coupon ? (
                  <Box display="flex" justifyContent="space-between" my={2}>
                    <Typography variant="h6">
                      <b>总计</b>
                    </Typography>

                    <Typography variant="h6">
                      <b>￥ {planData.totalPrice}</b>
                    </Typography>
                  </Box>
                ) : (
                  <>
                    <Box my={2}>
                      <TextField
                        fullWidth={true}
                        variant="outlined"
                        label="Coupon"
                        name="coupon"
                        disabled={true}
                        value={coupon.code}
                      />
                    </Box>

                    <Divider />

                    <Box display="flex" justifyContent="space-between" my={2}>
                      <Typography variant="h6">
                        <b>总计</b>
                      </Typography>

                      <Typography variant="h6">
                        <b>
                          <del>￥ {planData.totalPrice}</del>
                        </b>
                      </Typography>
                    </Box>

                    <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                      <Typography>
                        <b>优惠券</b>{' '}
                        <Chip
                          label={`可节省${coupon.percent}%`}
                          color="secondary"
                          size="small"
                          component="span"
                        />
                      </Typography>

                      <Typography variant="h6">
                        <b>￥ {calculateCouponTotalPrice()}</b>
                      </Typography>
                    </Box>
                  </>
                )}

                <ThemeButton variant="primary" isLoading={isBusy} type="submit">
                  确认购买
                </ThemeButton>
              </CardContent>
            </Card>

            <Typography className={classes.termsText} variant="body2">
              <input type="checkbox" required={true} className={classes.checkbox} />
              <a
                href={`${getEnv('MTG_URL_APP')}/blog/faq/terms-of-use-cn`}
                target="_blank"
                rel="noopener noreferrer"
              >
                <b>一旦提交订单，即默认您同意我们的使用条款及隐私政策。</b>
              </a>
            </Typography>
          </Grid>
        </Grid>
      </form>
    </>
  );
}

PageChina.propTypes = Props;
